import { Link, useLocation } from 'react-router';
import { ONBOARDING_STEPS, type TenantLifecycleStatus } from '@denwen-msp/shared';
import { useAuth } from '@/app/auth';
import { Banner } from '@/components/ui';
import { formatDate } from '@/lib/format';
import { useApi } from '@/lib/query';
import { useOnboarding } from './signup-api';

// Phase I (I-A): the strip above every staff page: the trial countdown, a payment problem, a
// restricted account, and (for owners) the unfinished onboarding wizard.

export interface Lifecycle {
  status: TenantLifecycleStatus;
  label: string;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  suspendedAt: string | null;
  deletionScheduledAt: string | null;
  graceEndsAt: string | null;
}

/** Where an owner chooses a plan (Phase I, I-B: Settings → Subscription). */
const PLAN_LINK = '/admin/subscription';
const EXPORT_LINK = '/admin/data-export';

export function LifecycleBanner() {
  const { user, can } = useAuth();
  const location = useLocation();
  const lifecycle = useApi<Lifecycle>(['lifecycle'], '/lifecycle', { enabled: user?.kind === 'staff' });
  const admin = can('admin.admin');
  const onboarding = useOnboarding(admin && user?.tenant.status === 'trial');
  const l = lifecycle.data;
  const banners = [];

  if (l?.status === 'trial' && l.trialDaysLeft !== null) {
    banners.push(
      <Banner key="trial" tone={l.trialDaysLeft <= 3 ? 'warning' : 'info'}>
        Free trial: {l.trialDaysLeft === 0 ? 'ends today' : `${l.trialDaysLeft} day${l.trialDaysLeft === 1 ? '' : 's'} left`}.{' '}
        {admin ? (
          <Link to={PLAN_LINK} className="font-medium underline">
            Choose a plan
          </Link>
        ) : null}
      </Banner>,
    );
  } else if (l?.status === 'past_due') {
    banners.push(
      <Banner key="past_due" tone="warning">
        A payment is overdue{l.graceEndsAt ? `. Everything keeps working until ${formatDate(l.graceEndsAt)}` : ''}.{' '}
        {admin ? (
          <Link to={PLAN_LINK} className="font-medium underline">
            Update your payment details
          </Link>
        ) : (
          'Ask an owner to update the payment details.'
        )}
      </Banner>,
    );
  } else if (l?.status === 'suspended') {
    banners.push(
      <Banner key="suspended" tone="danger">
        This account is suspended, so it is read-only: you can look things up and export data, but not make changes.{' '}
        {admin ? (
          <Link to={PLAN_LINK} className="font-medium underline">
            Choose a plan to carry on
          </Link>
        ) : null}
      </Banner>,
    );
  } else if (l?.status === 'cancelled' || l?.status === 'pending_deletion') {
    banners.push(
      <Banner key="closed" tone="danger">
        {l.status === 'cancelled' ? 'This account is cancelled.' : `This account will be deleted${l.deletionScheduledAt ? ` on ${formatDate(l.deletionScheduledAt)}` : ''}.`} You can still
        export your data.{' '}
        {admin ? (
          <Link to={EXPORT_LINK} className="font-medium underline">
            Export your data
          </Link>
        ) : null}
      </Banner>,
    );
  }

  const state = onboarding.data?.state;
  if (state && !state.completedAt && !state.dismissed && location.pathname !== '/onboarding') {
    const done = ONBOARDING_STEPS.filter((s) => state.steps[s.key] !== 'pending').length;
    banners.push(
      <Banner key="onboarding" tone="info">
        Finish setting up Denwen MSP: {done} of {ONBOARDING_STEPS.length} steps done.{' '}
        <Link to="/onboarding" className="font-medium underline">
          Continue
        </Link>
      </Banner>,
    );
  }

  if (!banners.length) return null;
  return <div className="space-y-2 px-6 pt-4">{banners}</div>;
}
